import { firebaseConfig, vapidKey } from './firebase-config';
import service from '../services';

let messagingInstance = null;
let currentToken = null;

async function getMessagingInstance() {
  if (!messagingInstance) {
    var { initializeApp } = await import(/* webpackChunkName: "firebase" */ 'firebase/app');
    var { getMessaging } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
    var app = initializeApp(firebaseConfig);
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
}

/**
 * Returns the current browser notification permission state.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function getNotificationPermissionState() {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request browser notification permission, get FCM token, and register with server.
 *
 * @param {string} authToken - JWT bearer token
 * @param {string} clientId - current client ID header value
 * @returns {Promise<string|null>} The FCM token or null on failure
 */
export async function requestNotificationPermission(authToken, clientId) {
  if (getNotificationPermissionState() === 'unsupported') {
    console.warn('[FCM] Push notifications not supported in this browser');
    return null;
  }

  try {
    var permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[FCM] Notification permission denied');
      return null;
    }
    return await registerFcmToken(authToken, clientId);
  } catch (err) {
    console.error('[FCM] Error requesting notification permission:', err);
    return null;
  }
}

/**
 * Silently register the FCM token when permission is already granted.
 *
 * @param {string} authToken - JWT bearer token
 * @param {string} clientId - current client ID header value
 * @returns {Promise<string|null>} The FCM token or null on failure
 */
export async function registerFcmToken(authToken, clientId) {
  if (getNotificationPermissionState() !== 'granted') {
    return null;
  }

  try {
    var swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    var { getToken } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');

    var messaging = await getMessagingInstance();
    var token = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      currentToken = token;
      await service.post('device-token/register', {
        token: token,
        platform: 'web',
      }, {
        'Authorization': authToken,
        'ClientId': clientId || '',
      });
      console.log('[FCM] Token registered');
    }
    return token;
  } catch (err) {
    console.error('[FCM] Error registering token:', err);
    return null;
  }
}

/**
 * Unregister the FCM token on logout or opt-out.
 *
 * @param {string} authToken - JWT bearer token
 * @param {string} clientId - current client ID header value
 */
export async function unregisterFcmToken(authToken, clientId) {
  try {
    if (currentToken) {
      await service.post('device-token/unregister', {
        token: currentToken,
      }, {
        'Authorization': authToken,
        'ClientId': clientId || '',
      }).catch(function () {});

      try {
        var { deleteToken } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
        var messaging = await getMessagingInstance();
        await deleteToken(messaging);
      } catch (e) {
        // Ignore — SW may already be unregistered
      }
      currentToken = null;
      console.log('[FCM] Token unregistered');
    }
  } catch (err) {
    console.error('[FCM] Error unregistering token:', err);
  }
}

/**
 * Set up foreground message handler.
 *
 * @param {object} vueInstance - A Vue component instance (needs this.$notify)
 */
export async function setupForegroundMessageHandler(vueInstance) {
  if (getNotificationPermissionState() !== 'granted') {
    return;
  }

  try {
    var { onMessage } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
    var messaging = await getMessagingInstance();
    onMessage(messaging, function (payload) {
      console.log('[FCM] Foreground message:', payload);

      var title = payload.notification
        ? payload.notification.title
        : (payload.data ? payload.data.title : 'Notification');
      var body = payload.notification
        ? payload.notification.body
        : (payload.data ? payload.data.body : '');

      if (vueInstance && vueInstance.$notify) {
        vueInstance.$notify.info({
          title: title,
          dangerouslyUseHTMLString: true,
          message: '<div style="max-height:200px;overflow-y:auto;white-space:pre-wrap;word-break:break-word;">' + body.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</div>',
          duration: 10000,
        });
      }
    });
  } catch (err) {
    console.error('[FCM] Error setting up foreground handler:', err);
  }
}

/**
 * Get the current FCM token stored in memory.
 * @returns {string|null}
 */
export function getCurrentToken() {
  return currentToken;
}
